import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { CheckCircle, ArrowRight, Lock, Phone, Calendar, Clock, MapPin, Download } from 'lucide-react';
import { PACKAGES, BRAND } from '../utils/constants';
import { saveLead, updateLead, markAbandoned } from '../utils/leads';
import {
  buildICS, downloadICS, buildGoogleCalendarLink,
  getAvailableSlots, getMinBookingDate,
} from '../utils/calendar';
import useSEO from '../hooks/useSEO';
import './Book.css';

/* ── Package prices in USD (for PayPal — JMD equiv approx) ── */
const PKG_USD = {
  consultation: 13,   // JMD 2,000 ≈ $13 USD
  online:        64,  // JMD 10,000 ≈ $64 USD
  inperson:      96,  // JMD 15,000 ≈ $96 USD
  dropin:        10,  // JMD 1,500 ≈ $10 USD
};

/* ── PayPal button wrapper ──────────────────────────────────── */
function PayPalPayButton({ pkg, lead, onSuccess, onError }) {
  const [{ isPending }] = usePayPalScriptReducer();

  if (isPending) {
    return <div className="paypal-loading">Loading PayPal…</div>;
  }

  return (
    <PayPalButtons
      style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay', height: 48 }}
      createOrder={(data, actions) =>
        actions.order.create({
          purchase_units: [{
            amount: {
              value: String(PKG_USD[pkg.id] || '10.00'),
              currency_code: 'USD',
            },
            description: `The Fhysique Factory — ${pkg.name} (Ref: ${lead?.refCode || ''})`,
            custom_id: lead?.refCode || '',
          }],
          application_context: {
            brand_name: 'The Fhysique Factory',
            shipping_preference: 'NO_SHIPPING',
          },
        })
      }
      onApprove={async (data, actions) => {
        const details = await actions.order.capture();
        // Fire webhook to n8n / Vercel API for 33/67 split
        try {
          await fetch('/api/paypal-capture', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              orderID: details.id,
              refCode: lead?.refCode,
              packageId: pkg.id,
              amountUSD: PKG_USD[pkg.id],
              clientName: lead?.name,
              clientEmail: lead?.email,
            }),
          });
        } catch (_) { /* non-blocking */ }
        onSuccess(details);
      }}
      onError={(err) => {
        console.error('PayPal error', err);
        onError('PayPal encountered an error. Please try again.');
      }}
      onCancel={() => onError('Payment cancelled.')}
    />
  );
}

/* ── Consultation date/time picker ──────────────────────────── */
function ConsultationPicker({ value, onChange }) {
  const minDate = getMinBookingDate();
  const slots = value.date ? getAvailableSlots(value.date) : [];

  return (
    <div className="consult-picker">
      <div className="form-group">
        <label><Calendar size={13} /> Preferred Date</label>
        <input
          type="date"
          min={minDate}
          value={value.date || ''}
          onChange={(e) => onChange({ date: e.target.value, time: '', isoTime: '' })}
          className="consult-date-input"
        />
        {value.date && (
          <span className="consult-weekday">
            {new Date(value.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </span>
        )}
      </div>

      {value.date && slots.length === 0 && (
        <p className="consult-no-slots">Weekends aren't available — please pick a weekday.</p>
      )}

      {slots.length > 0 && (
        <div className="form-group">
          <label><Clock size={13} /> Preferred Time</label>
          <div className="time-slots">
            {slots.map((s) => (
              <button
                key={s.time}
                type="button"
                className={`time-slot ${value.time === s.time ? 'selected' : ''}`}
                onClick={() => onChange({ ...value, time: s.time, isoTime: s.value })}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="consult-location">
        <MapPin size={13} />
        <span>{BRAND.location}</span>
      </div>
    </div>
  );
}

/* ── Main component ─────────────────────────────────────────── */
export default function Book() {
  const breadcrumbSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://fysiquefactory.fit/' },
      { '@type': 'ListItem', position: 2, name: 'Book Now', item: 'https://fysiquefactory.fit/book' },
    ],
  }), []);

  useSEO({
    title: 'Book Personal Trainer Kingston Jamaica | Pay Online | The Fhysique Factory',
    description: 'Book and pay for personal training sessions online at The Fhysique Factory Kingston Jamaica. Secure PayPal checkout. Consultation $2,000 JMD, online training $10,000/month, in-person $15,000/month. Instant confirmation.',
    path: '/book',
    schema: [breadcrumbSchema],
  });

  // steps: 1=details, 2=choose package, 3=pay, 4=confirm
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [errors, setErrors] = useState({});
  const [lead, setLead] = useState(null);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [consultation, setConsultation] = useState({ date: '', time: '', isoTime: '' });
  const [payError, setPayError] = useState('');

  // ── Abandoned checkout detection ──────────────────────────────
  // If user created a lead (step >= 2) but never paid (step !== 4),
  // mark as abandoned when they navigate away or close the tab.
  const completedRef = useRef(false);

  const handleAbandon = useCallback(() => {
    if (lead && !completedRef.current && step < 4) {
      markAbandoned(lead.id);
    }
  }, [lead, step]);

  useEffect(() => {
    // Mark completed when payment succeeds
    if (step === 4) completedRef.current = true;
  }, [step]);

  useEffect(() => {
    // Fire on tab close / navigate away
    const onBeforeUnload = () => handleAbandon();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      // Also fire on React unmount (navigate to different page)
      handleAbandon();
    };
  }, [handleAbandon]);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors((er) => ({ ...er, [e.target.name]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!form.phone.trim()) e.phone = 'Required';
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Invalid email';
    return e;
  };

  const handleStep1 = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    const newLead = saveLead({ ...form, source: 'booking-flow' });
    setLead(newLead);
    setStep(2);
  };

  const handleSelectPkg = (pkg) => {
    setSelectedPkg(pkg);
    setPayError('');
    setStep(3);
  };

  const handlePaySuccess = (details) => {
    // Update CRM
    updateLead(lead.id, {
      packageInterest: selectedPkg.id,
      status: 'Paid',
      paypalOrderId: details.id,
      consultationDate: consultation.isoTime || null,
    });

    // Send confirmation email via Gmail API
    if (lead.email) {
      fetch('/api/send-confirmation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientName:  lead.name,
          clientEmail: lead.email,
          packageName: selectedPkg.name,
          refCode:     lead.refCode,
          consultDate: consultation.isoTime
            ? new Date(consultation.isoTime).toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'short' })
            : null,
        }),
      }).catch(() => {});
    }

    // Trigger n8n / send-invite webhook for Gmail + calendar invite
    if (selectedPkg.id === 'consultation' && consultation.isoTime && lead.email) {
      fetch('/api/send-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientName:  lead.name,
          clientEmail: lead.email,
          refCode:     lead.refCode,
          startTime:   consultation.isoTime,
        }),
      }).catch(() => {});
    }

    setStep(4);
  };

  /* Calendar helpers for confirmation screen */
  const getCalendarLink = () => {
    if (!consultation.isoTime || !lead) return null;
    return buildGoogleCalendarLink({
      clientName: lead.name,
      refCode: lead.refCode,
      startDate: new Date(consultation.isoTime),
    });
  };

  const handleDownloadICS = () => {
    if (!consultation.isoTime || !lead) return;
    const ics = buildICS({
      clientName:  lead.name,
      clientEmail: lead.email,
      refCode:     lead.refCode,
      startDate:   new Date(consultation.isoTime),
    });
    downloadICS(ics);
  };

  const consultationDateLabel = consultation.isoTime
    ? new Date(consultation.isoTime).toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
    : null;

  const STEPS = ['Your Details', 'Choose Package', 'Secure Payment', 'Confirmation'];

  return (
    <div className="page book-page">
      <section className="page-hero">
        <div className="container">
          <span className="section-label">Secure Booking</span>
          <div className="lime-line" />
          <h1 className="section-title">Book Your Spot</h1>
          <p className="section-sub">
            All payments processed securely via PayPal. You'll receive a confirmation
            and your unique client reference number immediately.
          </p>
        </div>
      </section>

      {/* Progress bar */}
      <div className="book-progress">
        <div className="container">
          {STEPS.map((s, i) => (
            <div key={i} className={`progress-step ${step > i + 1 ? 'done' : ''} ${step === i + 1 ? 'active' : ''}`}>
              <div className="progress-dot">
                {step > i + 1 ? <CheckCircle size={14} /> : <span>{i + 1}</span>}
              </div>
              <span>{s}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Social proof strip — shown on steps 2 & 3 */}
      {(step === 2 || step === 3) && (
        <div className="book-proof-strip">
          <div className="container">
            <div className="bps-inner">
              <div className="bps-img-row">
                <img src="/images/transform-1.jpg" alt="Client transformation" loading="lazy" />
                <img src="/images/transform-3.jpg" alt="Client transformation" loading="lazy" />
                <img src="/images/transform-5.jpg" alt="Client transformation" loading="lazy" />
              </div>
              <div className="bps-quotes">
                <div className="bps-quote">
                  <span className="bps-stars">★★★★★</span>
                  <p>"Lost 18 lbs in 6 weeks — the meal plan alone is worth it." — Shandrika B.</p>
                </div>
                <div className="bps-quote">
                  <span className="bps-stars">★★★★★</span>
                  <p>"Down 22 lbs. My family thinks I went abroad." — Dwayne C.</p>
                </div>
                <div className="bps-quote">
                  <span className="bps-stars">★★★★★</span>
                  <p>"500+ clients transformed. Real results, no gimmicks." — 5.0 Google Rating</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container book-body">

        {/* ── Step 1: Details ── */}
        {step === 1 && (
          <div className="book-card">
            <h3>Enter Your Details</h3>
            <p className="text-muted" style={{ marginBottom: '24px', fontSize: '0.9rem' }}>
              We'll create your unique client reference code so every payment is tracked to you.
            </p>
            <form onSubmit={handleStep1}>
              <div className={`form-group ${errors.name ? 'error' : ''}`}>
                <label>Full Name *</label>
                <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Your full name" />
                {errors.name && <span className="form-error">{errors.name}</span>}
              </div>
              <div className={`form-group ${errors.phone ? 'error' : ''}`}>
                <label>Phone / WhatsApp *</label>
                <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="+1 (876) 000-0000" />
                {errors.phone && <span className="form-error">{errors.phone}</span>}
              </div>
              <div className={`form-group ${errors.email ? 'error' : ''}`}>
                <label>Email (for receipt & calendar invite)</label>
                <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="your@email.com" />
                {errors.email && <span className="form-error">{errors.email}</span>}
              </div>
              <button type="submit" className="btn btn-primary btn-full">
                Continue <ArrowRight size={16} />
              </button>
            </form>
          </div>
        )}

        {/* ── Step 2: Choose package ── */}
        {step === 2 && lead && (
          <div className="book-step2">
            <div className="ref-banner">
              <span className="ref-label">Your Client Reference</span>
              <span className="ref-code">{lead.refCode}</span>
              <span className="ref-note">Keep this — show it to Ramone and quote it in any message</span>
            </div>

            <h3 className="step2-title">Choose Your Package</h3>
            <p className="text-muted" style={{ marginBottom: '32px', fontSize: '0.9rem' }}>
              Payment is processed securely via PayPal. Prices shown in USD (equivalent to JMD rate).
            </p>

            <div className="book-packages">
              {PACKAGES.map((pkg) => (
                <div
                  key={pkg.id}
                  className={`book-pkg ${pkg.color === 'accent' ? 'featured' : ''} ${pkg.highlight ? 'highlighted' : ''}`}
                >
                  <div className="book-pkg-left">
                    {pkg.badge && <span className="pkg-badge-inline">{pkg.badge}</span>}
                    <div className="book-pkg-name">{pkg.name}</div>
                    <div className="book-pkg-price">
                      {pkg.price}<span>{pkg.period}</span>
                      <span className="pkg-usd-equiv"> · ~${PKG_USD[pkg.id]} USD</span>
                    </div>
                    {pkg.note && <div className="book-pkg-note">{pkg.note}</div>}
                    <ul className="book-pkg-features">
                      {pkg.features.slice(0, 3).map((f, i) => (
                        <li key={i}><CheckCircle size={12} /> {f}</li>
                      ))}
                      {pkg.features.length > 3 && (
                        <li className="more">+{pkg.features.length - 3} more included</li>
                      )}
                    </ul>
                    {/* Show date picker inline for consultation */}
                    {pkg.id === 'consultation' && (
                      <div className="inline-consult-picker" onClick={(e) => e.stopPropagation()}>
                        <p className="picker-label"><Calendar size={12} /> Pick your session date &amp; time</p>
                        <ConsultationPicker value={consultation} onChange={setConsultation} />
                      </div>
                    )}
                  </div>
                  <button
                    className={`btn ${pkg.color === 'accent' ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => handleSelectPkg(pkg)}
                    disabled={pkg.id === 'consultation' && (!consultation.date || !consultation.time)}
                  >
                    Pay {pkg.price}
                    {pkg.id === 'consultation' && !consultation.time && (
                      <span style={{ display: 'block', fontSize: '0.65rem', fontWeight: 400, opacity: 0.7 }}>Select date first</span>
                    )}
                  </button>
                </div>
              ))}
            </div>

            <div className="stripe-trust">
              <Lock size={13} />
              <span>Payments secured by PayPal · 256-bit SSL · Your card is never stored on our servers</span>
            </div>
          </div>
        )}

        {/* ── Step 3: PayPal payment ── */}
        {step === 3 && lead && selectedPkg && (
          <div className="book-card">
            <h3>Complete Payment</h3>
            <div className="pay-summary">
              <div className="pay-summary-row">
                <span>Package</span>
                <strong>{selectedPkg.name}</strong>
              </div>
              <div className="pay-summary-row">
                <span>Price (JMD)</span>
                <strong className="text-lime">{selectedPkg.price}{selectedPkg.period}</strong>
              </div>
              <div className="pay-summary-row">
                <span>Price (USD)</span>
                <strong>${PKG_USD[selectedPkg.id]}.00</strong>
              </div>
              {consultationDateLabel && (
                <div className="pay-summary-row">
                  <span>Session</span>
                  <strong>{consultationDateLabel}</strong>
                </div>
              )}
              <div className="pay-summary-row">
                <span>Client ID</span>
                <strong className="ref-code-sm">{lead.refCode}</strong>
              </div>
            </div>

            {payError && <p className="pay-error">{payError}</p>}

            <div className="paypal-wrap">
              <PayPalPayButton
                pkg={selectedPkg}
                lead={lead}
                onSuccess={handlePaySuccess}
                onError={setPayError}
              />
            </div>

            <div className="stripe-trust" style={{ marginTop: 16 }}>
              <Lock size={13} />
              <span>Secured by PayPal · Never share your card details outside PayPal's checkout</span>
            </div>

            <button className="link-btn" style={{ marginTop: 12 }} onClick={() => setStep(2)}>
              ← Back to packages
            </button>
          </div>
        )}

        {/* ── Step 4: Confirmation ── */}
        {step === 4 && lead && (
          <div className="book-card book-confirm">
            <CheckCircle size={52} className="confirm-icon" />
            <h3>Payment Confirmed!</h3>
            <p>
              Welcome to The Fhysique Factory, <strong>{lead.name}</strong>.
              Ramone will contact you within 2 hours to confirm your start.
            </p>

            <div className="confirm-ref">
              <span className="ref-label">Your Client ID</span>
              <span className="ref-code">{lead.refCode}</span>
              <span className="ref-note">Show this to Ramone — it links you to your payment</span>
            </div>

            {/* Consultation calendar section */}
            {selectedPkg?.id === 'consultation' && consultationDateLabel && (
              <div className="confirm-calendar">
                <div className="confirm-cal-header">
                  <Calendar size={18} className="text-lime" />
                  <h4>Your Session</h4>
                </div>
                <div className="confirm-cal-detail">
                  <Clock size={14} /><span>{consultationDateLabel}</span>
                </div>
                <div className="confirm-cal-detail">
                  <MapPin size={14} /><span>{BRAND.location}</span>
                </div>
                <div className="confirm-cal-actions">
                  <a
                    href={getCalendarLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-ghost btn-sm"
                  >
                    <Calendar size={14} /> Add to Google Calendar
                  </a>
                  <button className="btn btn-ghost btn-sm" onClick={handleDownloadICS}>
                    <Download size={14} /> Download .ics
                  </button>
                </div>
                {lead.email && (
                  <p className="confirm-email-note">
                    A calendar invite has been sent to <strong>{lead.email}</strong>
                  </p>
                )}
              </div>
            )}

            <div className="confirm-actions">
              <a
                href={`https://wa.me/${BRAND.whatsapp}?text=Hi! I just paid online. My client reference is ${lead.refCode}. Name: ${lead.name}.`}
                target="_blank" rel="noopener noreferrer"
                className="btn btn-whatsapp"
              >
                <Phone size={15} /> WhatsApp Ramone — Ref: {lead.refCode}
              </a>
            </div>

            <p className="confirm-note text-muted">
              Didn't complete payment?{' '}
              <button className="link-btn" onClick={() => setStep(3)}>Try again</button>
            </p>

            {/* IronLogic watermark */}
            <div className="confirm-built-by">
              Booking system by{' '}
              <a href="https://ironlogic.ai" target="_blank" rel="noopener noreferrer">⚡ IronLogic AI</a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
